import { useState, useMemo, useCallback, useEffect } from 'react'
import { ArrowLeft, Filter, Search, ChevronRight, AlertTriangle, CheckCircle2, Trash2, Check } from 'lucide-react'
import { useAppStore } from '@/store'
import type { SymbolNode, TagNode, Node } from '@/service/graph'
import { isNodeApproved } from '@/service/graph'
import { useTranslation } from '@/i18n'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'

interface PendingReviewPanelV2Props {
  isOpen: boolean
  onClose: () => void
  /** Reports the checked objects to spotlight + frame on the drawing (FS-523). */
  onHighlightChange?: (ids: Set<string>) => void
}

const EMPTY_SET: Set<string> = new Set()

interface SymbolGroup {
  name: string
  pendingCount: number
  approvedCount: number
  totalCount: number
  approved: boolean
}

interface TagGroup {
  content: string
  pendingCount: number
  approvedCount: number
  totalCount: number
  approved: boolean
}

type DetailView = { kind: 'symbol'; name: string } | { kind: 'tag'; content: string } | null

export default function PendingReviewPanelV2({ isOpen, onClose: _onClose, onHighlightChange }: PendingReviewPanelV2Props) {
  const { t } = useTranslation()
  const { currentState, updateNode } = useAppStore()
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterSection, setFilterSection] = useState<'symbols' | 'tags' | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSymbols, setSelectedSymbols] = useState<Set<string>>(new Set())
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())
  const [detailView, setDetailView] = useState<DetailView>(null)
  const [detailTab, setDetailTab] = useState<'all' | 'approved' | 'pending'>('all')
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set())

  // Group nodes by type
  const { symbolGroups, tagGroups, totalCount } = useMemo(() => {
    const symMap = new Map<string, { pending: number; approved: number }>()
    const tagMap = new Map<string, { pending: number; approved: number }>()

    for (const node of currentState.nodes.values()) {
      const isApproved = isNodeApproved(node)
      if (node.type === 'symbol') {
        const name = (node as SymbolNode).name || 'Unknown'
        const entry = symMap.get(name) || { pending: 0, approved: 0 }
        if (isApproved) entry.approved++; else entry.pending++
        symMap.set(name, entry)
      } else if (node.type === 'tag') {
        const content = (node as TagNode).content || 'Unknown'
        const entry = tagMap.get(content) || { pending: 0, approved: 0 }
        if (isApproved) entry.approved++; else entry.pending++
        tagMap.set(content, entry)
      }
    }

    const symbols: SymbolGroup[] = Array.from(symMap.entries())
      .map(([name, { pending, approved }]) => ({
        name, pendingCount: pending, approvedCount: approved,
        totalCount: pending + approved, approved: pending === 0 && approved > 0,
      }))
      .sort((a, b) => (a.approved === b.approved ? b.totalCount - a.totalCount : a.approved ? 1 : -1))

    const tags: TagGroup[] = Array.from(tagMap.entries())
      .map(([content, { pending, approved }]) => ({
        content, pendingCount: pending, approvedCount: approved,
        totalCount: pending + approved, approved: pending === 0 && approved > 0,
      }))
      .sort((a, b) => (a.approved === b.approved ? b.totalCount - a.totalCount : a.approved ? 1 : -1))

    return { symbolGroups: symbols, tagGroups: tags, totalCount: symMap.size + tagMap.size }
  }, [currentState.nodes])

  // Filtered groups for main list
  const filteredSymbols = symbolGroups.filter(s => {
    if (searchTerm && !s.name.toLowerCase().includes(searchTerm.toLowerCase())) return false
    if (selectedSymbols.size > 0 && !selectedSymbols.has(s.name)) return false
    return true
  })
  const filteredTags = tagGroups.filter(tg => {
    if (searchTerm && !tg.content.toLowerCase().includes(searchTerm.toLowerCase())) return false
    if (selectedTags.size > 0 && !selectedTags.has(tg.content)) return false
    return true
  })

  // Detail view: individual nodes for the selected group
  const detailNodes = useMemo(() => {
    if (!detailView) return []
    const nodes: (Node & { displayLabel: string })[] = []
    for (const node of currentState.nodes.values()) {
      if (detailView.kind === 'symbol' && node.type === 'symbol') {
        const sym = node as SymbolNode
        if (sym.name === detailView.name) {
          nodes.push({ ...node, displayLabel: sym.label || sym.name || node.id })
        }
      } else if (detailView.kind === 'tag' && node.type === 'tag') {
        const tag = node as TagNode
        if (tag.content === detailView.content) {
          nodes.push({ ...node, displayLabel: tag.content || node.id })
        }
      }
    }
    return nodes
  }, [currentState.nodes, detailView])

  const detailFiltered = detailNodes.filter(n => {
    if (detailTab === 'approved') return isNodeApproved(n)
    if (detailTab === 'pending') return !isNodeApproved(n)
    return true
  })

  const detailCounts = useMemo(() => ({
    all: detailNodes.length,
    approved: detailNodes.filter(n => isNodeApproved(n)).length,
    pending: detailNodes.filter(n => !isNodeApproved(n)).length,
  }), [detailNodes])

  // Spotlight + frame exactly the objects the user has checked. Empty when
  // nothing is checked, so the drawing isn't flooded with halos.
  const highlightIds = useMemo(
    () => (isOpen && selectedNodeIds.size > 0 ? selectedNodeIds : EMPTY_SET),
    [isOpen, selectedNodeIds],
  )

  useEffect(() => {
    onHighlightChange?.(highlightIds)
  }, [highlightIds, onHighlightChange])

  // Clear the drawing spotlight when the panel unmounts.
  useEffect(() => () => onHighlightChange?.(EMPTY_SET), [onHighlightChange])

  const handleApproveSelected = useCallback(() => {
    const ids = Array.from(selectedNodeIds)
    for (const id of ids) {
      const node = currentState.nodes.get(id)
      // ENG-2622: mark human-approved via the `approved` flag; leave the model's
      // `confidence` untouched so the prediction stays visible.
      if (node) updateNode({ ...node, approved: true })
    }
    setSelectedNodeIds(new Set())
    toast.success(t('graph_editor:nodePanel.approvedSuccess', { defaultValue: 'Approved Successfully' }))
  }, [selectedNodeIds, currentState.nodes, updateNode])

  const handleSelectAll = useCallback(() => {
    if (selectedNodeIds.size === detailFiltered.length) {
      setSelectedNodeIds(new Set())
    } else {
      setSelectedNodeIds(new Set(detailFiltered.map(n => n.id)))
    }
  }, [selectedNodeIds.size, detailFiltered])

  if (!isOpen) return null

  // ── Detail view: individual nodes ──
  if (detailView) {
    const groupName = detailView.kind === 'symbol' ? detailView.name : detailView.content
    return (
      <div className="w-[391px] h-full border-l border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card flex flex-col shrink-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 px-6 py-4 shrink-0">
          <button type="button" onClick={() => { setDetailView(null); setSelectedNodeIds(new Set()); setDetailTab('all') }}>
            <ArrowLeft className="w-4 h-4 text-foreground" />
          </button>
          <h3 className="text-base font-semibold text-foreground">{groupName}</h3>
        </div>

        {/* Tabs */}
        <div className="px-6 pb-3 shrink-0">
          <div className="bg-[#f5f5f5] dark:bg-dark-accent inline-flex items-center p-[3px] rounded-lg h-8">
            {(['all', 'approved', 'pending'] as const).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => { setDetailTab(tab); setSelectedNodeIds(new Set()) }}
                className={`px-3 py-1 text-sm rounded-md transition-all h-full ${
                  detailTab === tab
                    ? 'bg-white dark:bg-dark-card border border-[#e5e5e5] dark:border-dark-border shadow-sm font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'all' ? t('graph_editor:pendingReview.tabAll', { count: detailCounts.all, defaultValue: 'All ({{count}})' })
                  : tab === 'approved' ? t('graph_editor:pendingReview.tabApproved', { count: detailCounts.approved, defaultValue: 'Approved ({{count}})' })
                  : t('graph_editor:pendingReview.tabPending', { count: detailCounts.pending, defaultValue: 'Pending ({{count}})' })}
              </button>
            ))}
          </div>
        </div>

        {/* Node list */}
        <div className="flex-1 overflow-y-auto px-6">
          {/* Select All */}
          <label className="flex items-center gap-2 py-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              ref={(el) => {
                if (el) el.indeterminate = selectedNodeIds.size > 0 && selectedNodeIds.size < detailFiltered.length
              }}
              checked={selectedNodeIds.size > 0 && selectedNodeIds.size === detailFiltered.length}
              onChange={handleSelectAll}
              className="rounded border-gray-300 text-[#171717] focus:ring-[#171717]"
            />
            {t('graph_editor:pendingReview.selectAll', { defaultValue: 'Select All' })}
          </label>

          {detailFiltered.map(node => {
            const isApproved = isNodeApproved(node)
            return (
              <label key={node.id} className="flex items-center justify-between py-2 cursor-pointer">
                <div className="flex items-center gap-2 min-w-0">
                  <input
                    type="checkbox"
                    checked={selectedNodeIds.has(node.id)}
                    onChange={() => {
                      const next = new Set(selectedNodeIds)
                      if (next.has(node.id)) next.delete(node.id); else next.add(node.id)
                      setSelectedNodeIds(next)
                    }}
                    className="rounded border-gray-300 text-[#171717] focus:ring-[#171717] shrink-0"
                  />
                  <span className="text-sm text-foreground truncate">{node.displayLabel}</span>
                </div>
                {isApproved ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#dcfce7] text-[#15803d] border border-[#15803d] shrink-0">
                    <CheckCircle2 className="w-3 h-3" />
                    {t('graph_editor:pendingReview.approved', { defaultValue: 'Approved' })}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#fefce8] text-[#0a0a0a] border border-[#ca8a04] shrink-0">
                    <AlertTriangle className="w-3 h-3 text-[#ca8a04]" />
                    {t('graph_editor:pendingReview.needReview', { defaultValue: 'Need Review' })}
                  </span>
                )}
              </label>
            )
          })}
        </div>

        {/* Bottom action bar */}
        <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-[#e5e5e5] dark:border-dark-border shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
            disabled={selectedNodeIds.size === 0}
          >
            <Trash2 className="w-4 h-4" />
            {t('graph_editor:nodePanel.remove', { defaultValue: 'Remove' })}
          </Button>
          <Button
            size="sm"
            className="gap-1"
            disabled={selectedNodeIds.size === 0}
            onClick={handleApproveSelected}
          >
            <Check className="w-4 h-4" />
            {t('graph_editor:nodePanel.approve', { defaultValue: 'Approve' })}
          </Button>
        </div>
      </div>
    )
  }

  // ── Shared badge renderer ──
  const renderBadge = (group: { approved: boolean; pendingCount: number }) => {
    if (group.approved) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#dcfce7] text-[#15803d] border border-[#15803d] shrink-0">
          <CheckCircle2 className="w-3 h-3" />{t('graph_editor:pendingReview.approved', { defaultValue: 'Approved' })}
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#fefce8] text-[#0a0a0a] border border-[#ca8a04] shrink-0">
        <AlertTriangle className="w-3 h-3 text-[#ca8a04]" />{group.pendingCount}
      </span>
    )
  }

  // ── Main list view ──
  return (
    <div className="w-[391px] h-full border-l border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card flex flex-col shrink-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-[#e5e5e5] dark:border-dark-border shrink-0">
        <h3 className="text-base font-semibold text-[#0a0a0a] dark:text-foreground">
          {t('data_catalog:graph_editor.review_panel.title', { defaultValue: 'Object for Review' })}
        </h3>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          onClick={() => setFilterOpen(!filterOpen)}
        >
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">
            {t('common:buttons.filter', { defaultValue: 'Filter' })}
            <span className="text-[#737373]"> ({totalCount})</span>
          </span>
        </Button>
      </div>

      {/* Filter dropdown */}
      {filterOpen && (
        <div className="mx-6 mt-3 mb-3 border border-[#e5e5e5] dark:border-dark-border rounded-lg bg-white dark:bg-dark-card shadow-sm overflow-hidden">
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[#e5e5e5] dark:border-dark-border">
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={String(t('common:buttons.search', { defaultValue: 'Search' }))}
              className="text-sm bg-transparent border-none outline-none w-full placeholder:text-muted-foreground"
            />
          </div>
          {filterSection === null ? (
            <>
              <button type="button" onClick={() => setFilterSection('symbols')} className="flex items-center justify-between w-full px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors">
                <span>{t('graph_editor:pendingReview.symbolsCount', { count: symbolGroups.length, defaultValue: 'Symbols ({{count}})' })}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
              <button type="button" onClick={() => setFilterSection('tags')} className="flex items-center justify-between w-full px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors">
                <span>{t('graph_editor:pendingReview.tagsCount', { count: tagGroups.length, defaultValue: 'Tags ({{count}})' })}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => setFilterSection(null)} className="flex items-center gap-1.5 w-full px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors">
                <ArrowLeft className="w-4 h-4" /><span>{t('graph_editor:pendingReview.back', { defaultValue: 'Back' })}</span>
              </button>
              {filterSection === 'symbols' && (
                <div className="max-h-[296px] overflow-y-auto">
                  <label className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer">
                    <input type="checkbox" checked={selectedSymbols.size === 0} onChange={() => setSelectedSymbols(new Set())} className="rounded border-gray-300 text-[#171717] focus:ring-[#171717]" />
                    {t('graph_editor:pendingReview.allSymbol', { defaultValue: 'All Symbol' })}
                  </label>
                  {symbolGroups.map((s) => (
                    <label key={s.name} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer">
                      <input type="checkbox" checked={selectedSymbols.size === 0 || selectedSymbols.has(s.name)} onChange={() => { const next = new Set(selectedSymbols); if (next.has(s.name)) next.delete(s.name); else next.add(s.name); setSelectedSymbols(next) }} className="rounded border-gray-300 text-[#171717] focus:ring-[#171717]" />
                      {s.name}
                    </label>
                  ))}
                </div>
              )}
              {filterSection === 'tags' && (
                <div className="max-h-[296px] overflow-y-auto">
                  <label className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer">
                    <input type="checkbox" checked={selectedTags.size === 0} onChange={() => setSelectedTags(new Set())} className="rounded border-gray-300 text-[#171717] focus:ring-[#171717]" />
                    {t('graph_editor:pendingReview.allTags', { defaultValue: 'All Tags' })}
                  </label>
                  {tagGroups.map((tg) => (
                    <label key={tg.content} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer">
                      <input type="checkbox" checked={selectedTags.size === 0 || selectedTags.has(tg.content)} onChange={() => { const next = new Set(selectedTags); if (next.has(tg.content)) next.delete(tg.content); else next.add(tg.content); setSelectedTags(next) }} className="rounded border-gray-300 text-[#171717] focus:ring-[#171717]" />
                      {tg.content}
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
          <button type="button" onClick={() => { setSelectedSymbols(new Set()); setSelectedTags(new Set()); setSearchTerm(''); setFilterSection(null) }} className="w-full px-3 py-2 text-sm text-center text-muted-foreground hover:text-foreground border-t border-[#e5e5e5] dark:border-dark-border transition-colors">
            {t('graph_editor:pendingReview.clearAll', { defaultValue: 'Clear All' })}
          </button>
        </div>
      )}

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="flex flex-col gap-1.5">
          {/* Symbols header */}
          <div className="py-1.5">
            <span className="text-sm font-semibold text-[#0a0a0a] dark:text-foreground leading-5">{t('graph_editor:pendingReview.symbols', { defaultValue: 'Symbols' })}</span>
          </div>
          {filteredSymbols.map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() => { setDetailView({ kind: 'symbol', name: s.name }); setDetailTab('all') }}
              className="flex items-center justify-between p-1.5 w-full text-left hover:bg-gray-50 dark:hover:bg-dark-muted rounded-lg transition-colors"
            >
              <span className="text-sm font-medium text-blue-500 truncate">{s.name}</span>
              {renderBadge(s)}
            </button>
          ))}

          {/* Tags header */}
          <div className="py-1.5 mt-4">
            <span className="text-sm font-semibold text-[#0a0a0a] dark:text-foreground leading-5">{t('graph_editor:pendingReview.tags', { defaultValue: 'Tags' })}</span>
          </div>
          {filteredTags.map((tag) => (
            <button
              key={tag.content}
              type="button"
              onClick={() => { setDetailView({ kind: 'tag', content: tag.content }); setDetailTab('all') }}
              className="flex items-center justify-between p-1.5 w-full text-left hover:bg-gray-50 dark:hover:bg-dark-muted rounded-lg transition-colors"
            >
              <span className="text-sm font-medium text-blue-500 truncate mr-2">{tag.content}</span>
              {renderBadge(tag)}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
